import { pricingDestination } from "../lib/onboardingIntent";
import { signOut } from "firebase/auth";
import {
  ArrowRight,
  BarChart3,
  Battery,
  BatteryMedium,
  CalendarDays,
  CreditCard,
  FileEdit,
  Lightbulb,
  Loader2,
  Link2,
  LogOut,
  Menu,
  MessageCircleQuestion,
  Palette,
  Sparkles,
  X,
} from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import {
  isCheckoutContinuationUsable,
  useCheckoutContinuation,
} from "../hooks/useCheckoutContinuation";
import { useOnboardingProgress } from "../hooks/useOnboardingProgress";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { useIdeasBadge } from "../hooks/useIdeasBadge";
import { auth } from "../services/Firebase";
import { MobileSetupBadge } from "./MobileSetupBadge";
import { SetupPill } from "./SetupPill";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { needsPaymentRecovery } from "../utils/billingStatus";
import { WorkspacePeopleResponse } from "../models/Workspace";
import { workspaceRequiresSeatSelection } from "../utils/workspaceSeatSelection";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { WorkspaceUnavailable } from "./WorkspaceUnavailable";
import {
  BILLING_RETURN_INTENT_KEY,
  BillingReturnIntent,
  pendingEntitlementFingerprint,
  readBillingReturnIntent,
} from "../utils/billingReturnIntent";

const workspacesEnabled = process.env.REACT_APP_WORKSPACES_ENABLED === "true";
const BILLING_RETURN_STARTED_AT_KEY = "wavegen.billingReturnStartedAt";
const BILLING_RETURN_WINDOW_MS = 5 * 60_000;

// Context so children (e.g. CreatePost) can open the nav sidebar
const DashboardSidebarContext = createContext<{
  openSidebar: () => void;
} | null>(null);

export const useDashboardSidebar = () => useContext(DashboardSidebarContext);

interface DashboardLayoutProps {
  children: React.ReactNode;
  fullScreen?: boolean;
}

function formatCreditRefreshLabel(refreshAt?: number): string | null {
  if (!refreshAt) return null;

  const refreshDate = new Date(refreshAt);
  if (Number.isNaN(refreshDate.getTime())) return null;

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(refreshDate, today)) return "Refreshes today";
  if (sameDay(refreshDate, tomorrow)) return "Refreshes tomorrow";

  return `Refreshes ${refreshDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

function formatPlanEndingLabel(endsAt?: number): string {
  if (!endsAt) return "Renewal canceled";

  const endingDate = new Date(endsAt);
  if (Number.isNaN(endingDate.getTime())) return "Renewal canceled";

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(endingDate, today)) return "Plan ends today";
  if (sameDay(endingDate, tomorrow)) return "Plan ends tomorrow";

  return `Plan ends ${endingDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

const DashboardLayout = ({
  children,
  fullScreen = false,
}: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    currentUser,
    customerData,
    activeWorkspace,
    activeWorkspaceId,
    refreshWorkspaces,
    workspaceFeaturesEnabled,
    workspaceSwitching,
    workspaceBootstrapError,
    retryWorkspaceBootstrap,
  } = useAuth();
  // Desktop visibility comes from lg:translate-x-0; mobile starts unobstructed.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    availableCreditPacks,
    createCheckoutSession,
    createCreditCheckoutSession,
    createPaymentRecoverySession,
    createPortalSession,
    currentPlan,
    isHighestTier,
    isLoading: isStripeLoading,
    resumeCheckoutSession,
  } = useStripe();
  const { navigateToUpgrade } = useUpgradePage();
  const { toast } = useToast();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const onboardingProgress = useOnboardingProgress();
  const analyticsUiEnabled = useAnalyticsUiEnabled();
  const ideasBadge = useIdeasBadge(activeWorkspaceId, location.pathname);
  const [setupPulseTrigger, setSetupPulseTrigger] = useState(0);
  const [billingReturnStartedAt, setBillingReturnStartedAt] = useState<
    number | null
  >(() => {
    if (!readBillingReturnIntent()) return null;
    const stored = Number(
      window.sessionStorage.getItem(BILLING_RETURN_STARTED_AT_KEY),
    );
    return Number.isFinite(stored) &&
      Date.now() - stored < BILLING_RETURN_WINDOW_MS
      ? stored
      : null;
  });
  const [billingReturnIntent, setBillingReturnIntent] =
    useState<BillingReturnIntent | null>(() => readBillingReturnIntent());
  const billingReturnCheckInFlight = React.useRef(false);

  const clearBillingReturn = React.useCallback(() => {
    window.sessionStorage.removeItem(BILLING_RETURN_STARTED_AT_KEY);
    window.sessionStorage.removeItem(BILLING_RETURN_INTENT_KEY);
    setBillingReturnStartedAt(null);
    setBillingReturnIntent(null);
  }, []);

  useEffect(() => {
    if (searchParams.get("billing") !== "return") return;
    if (!activeWorkspaceId) return;

    const intent = readBillingReturnIntent();
    if (!intent || intent.workspaceId !== activeWorkspaceId) {
      clearBillingReturn();
      const cleanedParams = new URLSearchParams(searchParams);
      cleanedParams.delete("billing");
      navigate(
        {
          pathname: location.pathname,
          search: cleanedParams.toString()
            ? `?${cleanedParams.toString()}`
            : "",
          hash: location.hash,
        },
        { replace: true },
      );
      return;
    }

    const startedAt = Date.now();
    window.sessionStorage.setItem(
      BILLING_RETURN_STARTED_AT_KEY,
      String(startedAt),
    );
    setBillingReturnStartedAt(startedAt);
    setBillingReturnIntent(intent);

    const cleanedParams = new URLSearchParams(searchParams);
    cleanedParams.delete("billing");
    navigate(
      {
        pathname: location.pathname,
        search: cleanedParams.toString()
          ? `?${cleanedParams.toString()}`
          : "",
        hash: location.hash,
      },
      { replace: true },
    );
    void refreshWorkspaces().catch((error) =>
      console.error("Could not refresh billing return state:", error),
    );
  }, [
    activeWorkspaceId,
    clearBillingReturn,
    location.hash,
    location.pathname,
    navigate,
    refreshWorkspaces,
    searchParams,
  ]);

  useEffect(() => {
    if (!billingReturnStartedAt) return;
    const remaining =
      BILLING_RETURN_WINDOW_MS - (Date.now() - billingReturnStartedAt);
    if (remaining <= 0) {
      clearBillingReturn();
      return;
    }
    const timeout = window.setTimeout(clearBillingReturn, remaining);
    return () => window.clearTimeout(timeout);
  }, [billingReturnStartedAt, clearBillingReturn]);

  useEffect(() => {
    if (!billingReturnStartedAt || !billingReturnIntent) return;
    const currentFingerprint = pendingEntitlementFingerprint(
      customerData?.pendingTeamEntitlement,
    );
    if (
      customerData?.pendingTeamEntitlement &&
      currentFingerprint !== billingReturnIntent.pendingEntitlementFingerprint
    ) {
      return;
    }
    const interval = window.setInterval(() => {
      void refreshWorkspaces().catch((error) =>
        console.error("Could not poll billing return state:", error),
      );
    }, 3_000);
    return () => window.clearInterval(interval);
  }, [
    billingReturnStartedAt,
    billingReturnIntent,
    customerData?.pendingTeamEntitlement,
    refreshWorkspaces,
  ]);

  useEffect(() => {
    if (
      !billingReturnStartedAt ||
      !billingReturnIntent ||
      !customerData?.pendingTeamEntitlement ||
      pendingEntitlementFingerprint(customerData.pendingTeamEntitlement) ===
        billingReturnIntent.pendingEntitlementFingerprint ||
      typeof customerData.pendingTeamEntitlement.confirmedAt === "number" ||
      !currentUser ||
      !activeWorkspaceId ||
      activeWorkspace?.role !== "owner" ||
      billingReturnCheckInFlight.current
    ) {
      return;
    }

    billingReturnCheckInFlight.current = true;
    const checkRequiredSeats = async () => {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/workspaces/${encodeURIComponent(activeWorkspaceId)}/people`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": activeWorkspaceId,
            },
          },
        );
        if (!response.ok) {
          throw new Error("Could not check teammate seats after billing.");
        }
        const people = (await response.json()) as WorkspacePeopleResponse;
        if (
          Number(
            window.sessionStorage.getItem(BILLING_RETURN_STARTED_AT_KEY),
          ) !== billingReturnStartedAt
        ) {
          return;
        }

        if (workspaceRequiresSeatSelection(people)) {
          const confirmationResponse = await fetch(
            `${process.env.REACT_APP_API_BASE}/api/workspaces/${encodeURIComponent(activeWorkspaceId)}/seats`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "x-workspace-id": activeWorkspaceId,
              },
              body: JSON.stringify({
                selectedMemberIds:
                  people.pendingTeamEntitlement?.selectedMemberIds || [],
              }),
            },
          );
          if (!confirmationResponse.ok) {
            throw new Error(
              "Could not confirm the default teammate seats after billing.",
            );
          }
          clearBillingReturn();
          navigate("/workspace?focus=teammates#teammate-access", {
            replace: true,
          });
          return;
        }
        clearBillingReturn();
      } catch (error) {
        console.error("Could not check billing return teammate seats:", error);
        clearBillingReturn();
        navigate("/workspace#teammate-access", { replace: true });
      } finally {
        billingReturnCheckInFlight.current = false;
      }
    };
    void checkRequiredSeats();
  }, [
    activeWorkspace?.role,
    activeWorkspaceId,
    billingReturnStartedAt,
    billingReturnIntent,
    clearBillingReturn,
    currentUser,
    customerData?.pendingTeamEntitlement,
    navigate,
  ]);

  const videoCredits =
    (customerData?.videoCredits ?? 0) + (customerData?.bonusCredits ?? 0);
  const paymentStatus =
    customerData?.paymentStatus || currentPlan?.paymentStatus;
  const paymentNeedsAttention = needsPaymentRecovery(
    paymentStatus,
    currentPlan?.subscriptionStatus,
  );
  const paymentNeedsCompletion =
    paymentStatus === "requires_action" ||
    currentPlan?.subscriptionStatus === "incomplete";
  const hasActiveSubscription =
    !!currentPlan?.stripePriceId && !isStripeLoading;
  const checkoutContinuation = useCheckoutContinuation(customerData);
  const actionableCheckoutContinuation =
    checkoutContinuation?.mode === "subscription" && hasActiveSubscription
      ? undefined
      : checkoutContinuation;
  const isCanceledSubscription =
    hasActiveSubscription && !!currentPlan?.cancelAtPeriodEnd;
  const creditRefreshLabel = hasActiveSubscription
    ? formatCreditRefreshLabel(currentPlan?.creditRefreshAt)
    : null;
  const subscriptionEndingLabel =
    isCanceledSubscription && !creditRefreshLabel
      ? formatPlanEndingLabel(
          currentPlan?.cancelAt || currentPlan?.currentPeriodEndAt,
        )
      : null;
  const creditStatusLabel = subscriptionEndingLabel || creditRefreshLabel;
  const smallestCreditPack = React.useMemo(
    () =>
      [...availableCreditPacks].sort(
        (a, b) => a.video_credits - b.video_credits,
      )[0],
    [availableCreditPacks],
  );

  const handleCheckoutContinuation = async () => {
    if (!actionableCheckoutContinuation) return;

    const fallbackToFreshCheckout = async () => {
      if (!actionableCheckoutContinuation.priceId) {
        navigateToUpgrade({
          reason:
            actionableCheckoutContinuation.mode === "payment"
              ? "no_credits"
              : "sidebar_cta",
        });
        return;
      }

      if (actionableCheckoutContinuation.mode === "subscription") {
        await createCheckoutSession(actionableCheckoutContinuation.priceId);
        return;
      }

      await createCreditCheckoutSession(actionableCheckoutContinuation.priceId);
    };

    if (!isCheckoutContinuationUsable(actionableCheckoutContinuation)) {
      await fallbackToFreshCheckout();
      return;
    }

    try {
      if (
        actionableCheckoutContinuation.kind === "recovery" &&
        actionableCheckoutContinuation.recoveryUrl
      ) {
        window.location.href = actionableCheckoutContinuation.recoveryUrl;
        return;
      }

      await resumeCheckoutSession(actionableCheckoutContinuation.sessionId);
    } catch {
      await fallbackToFreshCheckout();
    }
  };

  // Determine text and action for the main CTA button (Billing/Upgrade).
  // mainCtaProminent => render the rich "branded card" layout (posts remaining
  // + credit status + arrow), matching "Upgrade for more". The plain ghost
  // button is reserved for non-conversion states: Billing Portal and loading.
  let mainCtaText = "Upgrade Plan";
  let mainCtaAction: () => void | Promise<void> = () =>
    navigateToUpgrade({ reason: "sidebar_cta" });
  let mainCtaProminent = true;

  if (paymentNeedsAttention && !isStripeLoading) {
    mainCtaText = paymentNeedsCompletion
      ? "Complete payment"
      : "Resolve payment";
    mainCtaAction = handlePaymentRecovery;
  } else if (actionableCheckoutContinuation && !isStripeLoading) {
    mainCtaText = "Continue checkout";
    mainCtaAction = handleCheckoutContinuation;
  } else if (isStripeLoading) {
    mainCtaText = "Loading...";
    mainCtaAction = () => {}; // Disable action while loading
    mainCtaProminent = false;
  } else if (currentPlan && currentPlan.stripePriceId) {
    // User has an active Stripe subscription
    if (isCanceledSubscription) {
      mainCtaText = "Reactivate plan";
      mainCtaAction = () => handlePortalSession();
    } else if (videoCredits > 0) {
      mainCtaText = "Billing Portal";
      mainCtaAction = () => handlePortalSession();
      mainCtaProminent = false;
    } else {
      // videoCredits === 0, but has an active Stripe subscription
      if (smallestCreditPack) {
        mainCtaText = "Get more credits";
        mainCtaAction = () =>
          navigateToUpgrade({ reason: "no_credits", hash: "credit-packs" });
      } else if (isHighestTier) {
        // On highest Stripe tier, 0 credits. Billing Portal to manage. Support button for credit request.
        mainCtaText = "Billing Portal";
        mainCtaAction = () => handlePortalSession();
        mainCtaProminent = false;
      } else {
        // Not highest Stripe tier, 0 credits. Needs to upgrade their Stripe plan.
        mainCtaText = "Upgrade for more";
        mainCtaAction = () => navigateToUpgrade({ reason: "no_credits" });
      }
    }
  } else {
    // No active Stripe subscription (currentPlan.stripePriceId is null).
    // This covers legacy users with DB plans, or actual free users.
    // To get video credits or more video credits, they need a Stripe plan.
    mainCtaText = "Upgrade for more";
    mainCtaAction = () => navigateToUpgrade({ reason: "sidebar_cta" });
  }
  const canManageBilling = activeWorkspace?.role === "owner";
  if (!canManageBilling) mainCtaProminent = false;
  // Free-plan owners with nothing pending: the plain "go upgrade" state.
  const showMobileUpgradeCta =
    canManageBilling &&
    !isStripeLoading &&
    !paymentNeedsAttention &&
    !actionableCheckoutContinuation &&
    !currentPlan?.stripePriceId;

  // Determine text for the Support button
  let supportButtonText = "Support";
  if (
    !isStripeLoading &&
    currentPlan &&
    currentPlan.stripePriceId &&
    videoCredits === 0 &&
    isHighestTier &&
    !isCanceledSubscription
  ) {
    // User has an active Stripe subscription, is on the highest Stripe tier, and has 0 credits
    supportButtonText = "Request More Credits";
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handlePortalSession = async (
    flow?: "payment_method_update",
  ) => {
    setIsLoadingPortal(true);
    try {
      await createPortalSession(flow);
    } catch {
      toast({
        title: "Couldn't open billing",
        description: "Please try again. If this continues, contact support.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  async function handlePaymentRecovery() {
    setIsLoadingPortal(true);
    try {
      await createPaymentRecoverySession();
    } catch {
      toast({
        title: "Couldn't open payment recovery",
        description: "Please try again. If this continues, contact support.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPortal(false);
    }
  }

  // Effect to check for planId on load and redirect to upgrade page
  useEffect(() => {
    const sessionPlanId = window.sessionStorage.getItem("pendingPlanId");
    const urlPlanId = searchParams.get("planId");
    const planId = sessionPlanId || urlPlanId;

    if (activeWorkspace?.role !== "owner") {
      if (sessionPlanId) window.sessionStorage.removeItem("pendingPlanId");
      return;
    }

    if (currentUser && planId) {
      if (sessionPlanId) {
        window.sessionStorage.removeItem("pendingPlanId");
      }
      const back = new URL(window.location.href);
      back.searchParams.delete("planId");
      navigate(pricingDestination(planId, `${back.pathname}${back.search}${back.hash}`), { replace: true });
    } else {
      if (sessionPlanId) {
        window.sessionStorage.removeItem("pendingPlanId");
      }
    }
  }, [
    currentUser,
    activeWorkspace?.role,
    searchParams,
    navigate,
    navigateToUpgrade,
    location.state,
  ]);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && sidebarOpen) setSidebarOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sidebarOpen]);

  if (!activeWorkspace) {
    return (
      <WorkspaceUnavailable
        bootstrapError={workspaceBootstrapError}
        onRetry={retryWorkspaceBootstrap}
      />
    );
  }

  const navigationItems: {
    icon: typeof FileEdit;
    label: string;
    path: string;
    badge?: string;
    badgeLabel?: string;
  }[] = [
    {
      icon: Lightbulb,
      label: "Ideas",
      path: "/ideas",
      ...(ideasBadge.count
        ? {
            badge: ideasBadge.count > 99 ? "99+" : String(ideasBadge.count),
            badgeLabel: `${ideasBadge.count} new ${ideasBadge.count === 1 ? "idea" : "ideas"}`,
          }
        : ideasBadge.finding
          ? { badge: "Finding…", badgeLabel: "Finding ideas" }
          : {}),
    },
    { icon: FileEdit, label: "Drafts", path: "/created" },
    { icon: CalendarDays, label: "Posts", path: "/dashboard" },
    ...(analyticsUiEnabled
      ? [{ icon: BarChart3, label: "Analytics", path: "/analytics" }]
      : []),
    { icon: Palette, label: "Brand Kits", path: "/brand-kits" },
    { icon: Link2, label: "Connected Accounts", path: "/settings" },
  ];

  // Sidebar content shared between normal and fullScreen modes
  const sidebarContent = (
    <nav aria-label="Main navigation" className="flex flex-col h-full">
      {/* Header section */}
      <div className="border-b border-border p-3">
        <div className="mb-3 flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <img src="/icon.svg" alt="WaveGen Logo" className="h-6 w-auto" />
            <span className="text-foreground font-semibold">
              WaveGen<span className="text-brand">.ai</span>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(false)}
            className={fullScreen ? "" : "lg:hidden"}
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {workspacesEnabled && workspaceFeaturesEnabled && <WorkspaceSwitcher />}
      </div>

      {/* Navigation Items */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="mb-4 space-y-2">
          <Button
            variant="brand"
            onClick={() => {
              navigate("/generate");
              setSidebarOpen(false);
            }}
            className="w-full"
          >
            <Sparkles className="h-4 w-4" />
            <span className="ml-2">Generate Content</span>
          </Button>
        </div>
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
              >
                <item.icon className="h-4 w-4" />
                <span className="ml-2">{item.label}</span>
                {item.badge && (
                  <span
                    className={
                      item.badge === "Finding…"
                        ? "ml-auto text-xs font-normal text-muted-foreground"
                        : "ml-auto rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-brand-ink"
                    }
                  >
                    <span aria-hidden="true">{item.badge}</span>
                    <span className="sr-only">{item.badgeLabel}</span>
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="p-4 space-y-1.5">
        {canManageBilling && (
          <SetupPill
            progress={onboardingProgress}
            pulseTrigger={setupPulseTrigger}
            onStepClick={() => setSidebarOpen(false)}
          />
        )}
        {mainCtaProminent ? (
          /* Conversion CTA: branded card merging credits + action */
          <button
            onClick={mainCtaAction}
            disabled={isStripeLoading || isLoadingPortal}
            aria-label={
              paymentNeedsAttention
                ? `Payment needs attention. ${mainCtaText}`
                : `${videoCredits} post${videoCredits !== 1 ? "s" : ""} remaining. ${
                    creditStatusLabel ? `${creditStatusLabel}. ` : ""
                  }${mainCtaText}`
            }
            className={`w-full rounded-xl border p-3.5 text-left transition-all disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              paymentNeedsAttention
                ? "border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100/70"
                : "border-brand/20 bg-brand/5 hover:border-brand/30 hover:bg-brand/10"
            }`}
          >
            {paymentNeedsAttention ? (
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-amber-950">
                  <CreditCard className="h-4 w-4 shrink-0 text-amber-700" />
                  <span>
                    {paymentNeedsCompletion
                      ? "Payment needs completion"
                      : "Payment failed"}
                  </span>
                </p>
                <p className="mt-1.5 text-xs leading-5 text-amber-900">
                  Resolve your billing details to keep your plan in good
                  standing.
                </p>
              </div>
            ) : (
              <>
                <p className="text-lg font-semibold text-foreground leading-none">
                  <span>{videoCredits}</span>{" "}
                  <span className="text-xs font-medium text-muted-foreground ml-1.5">
                    {`post${videoCredits !== 1 ? "s" : ""} remaining`}
                  </span>
                </p>
                {creditStatusLabel && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" />
                    <span>{creditStatusLabel}</span>
                  </p>
                )}
              </>
            )}
            <div
              className={`flex items-center justify-between mt-3 pt-3 border-t ${
                paymentNeedsAttention
                  ? "border-amber-200"
                  : "border-brand/10"
              }`}
            >
              <span
                className={`text-sm font-semibold ${
                  paymentNeedsAttention ? "text-amber-800" : "text-brand"
                }`}
              >
                {mainCtaText}
              </span>
              <ArrowRight
                className={`h-3.5 w-3.5 ${
                  paymentNeedsAttention ? "text-amber-700" : "text-brand/60"
                }`}
              />
            </div>
          </button>
        ) : (
          /* Paid user */
          <>
            <div
              className={`w-full rounded-lg px-3 py-2.5 text-sm font-medium
                ${
                  isCanceledSubscription && videoCredits > 0
                    ? "border border-amber-200 bg-amber-50 text-amber-950"
                    : videoCredits > 0
                      ? "bg-muted text-foreground"
                      : "bg-red-100 text-red-700"
                }`}
            >
              <div className="flex items-center">
                {videoCredits > 0 ? (
                  <BatteryMedium
                    className={`h-4 w-4 shrink-0 ${
                      isCanceledSubscription
                        ? "text-amber-700"
                        : "text-muted-foreground"
                    }`}
                  />
                ) : (
                  <Battery className="h-4 w-4 shrink-0 text-red-600" />
                )}
                <span className="ml-1.5">
                  {`${videoCredits} post${videoCredits !== 1 ? "s" : ""} available`}
                </span>
              </div>
              {creditStatusLabel && (
                <div
                  className={`mt-1.5 flex items-center gap-1.5 text-xs font-normal ${
                    isCanceledSubscription && videoCredits > 0
                      ? "text-amber-800"
                      : videoCredits > 0
                        ? "text-muted-foreground"
                        : "text-red-700/80"
                  }`}
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  <span>{creditStatusLabel}</span>
                </div>
              )}
            </div>
            {canManageBilling && (
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={mainCtaAction}
                disabled={isStripeLoading || isLoadingPortal}
              >
                <CreditCard className="h-4 w-4" />
                <span className="ml-2">{mainCtaText}</span>
              </Button>
            )}
          </>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={() => navigate("/feedback")}
        >
          <MessageCircleQuestion className="h-4 w-4" />
          <span className="ml-2">{supportButtonText}</span>
        </Button>
      </div>

      {/* User Info + Sign Out */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
            {currentUser?.photoURL ? (
              <img
                src={currentUser.photoURL}
                alt={currentUser.email || "User"}
                className="h-full w-full rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${currentUser?.displayName?.charAt(0) || "U"}`;
                }}
              />
            ) : (
              currentUser?.email?.charAt(0).toUpperCase()
            )}
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            <span className="truncate text-sm text-foreground">
              {currentUser?.displayName}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {currentUser?.email}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            title="Sign out"
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </nav>
  );

  return (
    <DashboardSidebarContext.Provider
      value={{ openSidebar: () => setSidebarOpen(true) }}
    >
      <div className="min-h-screen bg-secondary flex">
        {isLoadingPortal && (
          <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50">
            <div className="bg-background p-4 rounded-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Opening secure billing...</span>
            </div>
          </div>
        )}

          {/* Sidebar Overlay Backdrop (mobile in normal mode, always in fullScreen) */}
          {sidebarOpen && (
            <div
              className={`fixed inset-0 bg-foreground/50 z-30 ${fullScreen ? "" : "lg:hidden"}`}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`
            fixed top-0 left-0
            bg-background border-r border-border
            transition-transform duration-200
            flex flex-col h-[100svh] w-64
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
            z-40
            pb-[env(safe-area-inset-bottom)]
            ${!fullScreen ? "lg:translate-x-0 lg:z-30" : ""}
          `}
          >
            {sidebarContent}
          </div>

          {/* Mobile Header (normal mode only) */}
          {!fullScreen && (
            <div className="fixed top-0 left-0 right-0 h-16 bg-background border-b border-border flex items-center justify-between px-4 lg:hidden z-10">
              <div className="flex flex-1 items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(true)}
                  aria-label={
                    ideasBadge.count
                      ? `Open navigation, ${ideasBadge.count} new ${ideasBadge.count === 1 ? "idea" : "ideas"}`
                      : "Open navigation"
                  }
                  className="relative"
                >
                  <Menu className="h-4 w-4" />
                  {/* New ideas wait in the drawer; show that before it opens. */}
                  {!!ideasBadge.count && location.pathname !== "/ideas" && (
                    <span
                      aria-hidden="true"
                      className="absolute right-1 top-1 h-2 w-2 rounded-full bg-brand ring-2 ring-background"
                    />
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <img
                  src="/icon.svg"
                  alt="WaveGen Logo"
                  className="h-6 w-auto"
                />
              </div>
              <div className="flex flex-1 items-center justify-end gap-2">
                {/* The drawer no longer auto-opens on mobile, so free owners
                    keep a visible upgrade entry point in the header. */}
                {showMobileUpgradeCta && (
                  <Button
                    size="sm"
                    onClick={() =>
                      navigateToUpgrade({ reason: "mobile_header" })
                    }
                    className="h-8 rounded-full px-3 text-xs font-semibold"
                  >
                    Upgrade
                  </Button>
                )}
                {canManageBilling && (
                  <MobileSetupBadge
                    progress={onboardingProgress}
                    onTap={() => {
                      setSidebarOpen(true);
                      setSetupPulseTrigger((prev) => prev + 1);
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <main
            className={`
            flex-1
            transition-all
            duration-200
            ${
              fullScreen
                ? "h-screen w-full overflow-hidden p-0 m-0"
                : `
              pt-16 lg:pt-6
              px-4 lg:px-6
              lg:ml-64
              min-w-0 overflow-x-hidden
            `
            }
          `}
          >
            {paymentNeedsAttention && !fullScreen && (
              <div
                role="alert"
                className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                  <div>
                    <p className="text-sm font-semibold">
                      {paymentNeedsCompletion
                        ? "Your payment needs completion"
                        : "We couldn't process your payment"}
                    </p>
                    <p className="mt-0.5 text-sm text-amber-900">
                      Resolve your billing details to keep your WaveGen plan in
                      good standing.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handlePaymentRecovery}
                  disabled={isLoadingPortal}
                  className="shrink-0 border-amber-400 bg-white text-amber-950 hover:bg-amber-100 hover:text-amber-950"
                >
                  <CreditCard className="h-4 w-4" />
                  <span className="ml-2">{mainCtaText}</span>
                </Button>
              </div>
            )}
            {workspaceSwitching ? (
              <div className="flex min-h-[50vh] items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening workspace…
              </div>
            ) : (
              <React.Fragment key={activeWorkspaceId || "no-workspace"}>
                {children}
              </React.Fragment>
            )}
          </main>
      </div>
    </DashboardSidebarContext.Provider>
  );
};

export default DashboardLayout;
